/* eslint-disable react/jsx-no-useless-fragment */
import OverdueSubscriptionStickyBanner from "components/billing/OverdueSubscriptionStickyBanner";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import { CompanyContextEnum, CompanyMenuSlugEnum } from "components/company/v3/menu/enums";
import getAmericanMenus from "components/company/v3/menu/helpers/getAmericanMenus";
import getBrazilianMenus from "components/company/v3/menu/helpers/getBrazilianMenus";
import getFiisMenus from "components/company/v3/menu/helpers/getFiisMenus";
import getTickerSymbolSwitchUrl from "components/company/v3/menu/helpers/getTickerSymbolSwitchUrl";
import { useCompanyNavbarMobileIsOpen } from "components/company/v3/menu/hooks";
import type { MenuOption } from "components/company/v3/menu/types";
import Avatar from "components/core/Avatar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { Provider } from "jotai";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { Selfie } from "lib/hooks/useSelfie";
import useSelfie from "lib/hooks/useSelfie";
import { ChevronDownIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { useState } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { IPlatformVertical } from "types/graphqlTypes";
import { format } from "util";
import {
  CompanyNavbarContent,
  CompanyNavbarMenuOption,
  CompanyNavbarPortal,
  MobileCompanyNavbarContent,
  MobileCompanyNavbarMenuOption,
  MobileCompanyNavbarTrigger,
} from "../menu/CompanyNavbar";

interface CompanyNavbarLayoutProps {
  children: React.ReactNode;
  context: CompanyContextEnum;
  tickerSymbol: string;
}

export default function CompanyNavbarLayout({ children, context, tickerSymbol }: CompanyNavbarLayoutProps) {
  const issuer = useRootIssuer();

  const intl = useIntl();
  const pathname = usePathname();
  const viewer = useSelfie();
  const menus = getMenuOptions(viewer, tickerSymbol, context, intl);

  return (
    <Provider>
      <div className="flex flex-col">
        <CompanyNavbarPortal>
          <CompanyNavbarContent className="hidden justify-center dark:bg-slate-900 lg:flex">
            <div className="flex flex-row items-center gap-x-2">
              <CompanyNavbarMenuOption
                key="companyNavbar/home"
                url={getHomeUrl(tickerSymbol, context)}
                isActive={false}
              >
                <Avatar
                  src={issuer.niceLogo?.url || null}
                  hashValue={issuer._id}
                  alt={issuer.name}
                  size={42}
                  squircle={true}
                  className="md:inline-flex"
                  priority={true}
                />
              </CompanyNavbarMenuOption>

              <TickerSymbolSwitch tickerSymbol={tickerSymbol} context={context} />
            </div>

            {menus.map((menu) => {
              if (menu.render) {
                const Component = menu.render;

                return (
                  <Component
                    key={format("%s/companyNavbar/%s", issuer._id, menu.label)}
                    icon={menu.icon}
                    label={menu.label}
                  />
                );
              }

              if (!menu.href) {
                return null;
              }

              const isActive = pathname === menu.href;
              const isAssetDiscussionBox = menu.slug === CompanyMenuSlugEnum.DISCUSSIONS;

              return (
                <CompanyNavbarMenuOption
                  key={format("%s/companyNavbar/%s", issuer._id, menu.label)}
                  url={menu.href}
                  isActive={isActive}
                >
                  <div>{menu.icon}</div>
                  <span className="text-xs dark:text-slate-300">{menu.label}</span>

                  {isAssetDiscussionBox && <CompanyDiscussionsCouner />}
                </CompanyNavbarMenuOption>
              );
            })}
          </CompanyNavbarContent>

          <CompanyNavbarContent className="relative flex flex-col gap-3 dark:bg-slate-900 lg:hidden">
            <MobileCompanyNavbarHeader tickerSymbol={tickerSymbol} context={context} menus={menus} />

            <MobileCompanyNavbarContent>
              {menus.map((menu) => {
                const isAssetDiscussionBox = menu.slug === CompanyMenuSlugEnum.DISCUSSIONS;

                if (menu.render) {
                  const Component = menu.render;

                  return (
                    <Component
                      key={format("%s/companyNavbar/%s", issuer._id, menu.label)}
                      icon={menu.icon}
                      label={menu.label}
                    />
                  );
                }

                if (!menu.href) {
                  return null;
                }

                return (
                  <MobileCompanyNavbarMenuOption
                    key={format("%s/companyNavbar/%s", issuer._id, menu.label)}
                    href={menu.href}
                    icon={
                      <>
                        {isAssetDiscussionBox && <CompanyDiscussionsCouner />}
                        {menu.icon}
                      </>
                    }
                    title={menu.label}
                    iconClassName="relative border-blue-200 bg-blue-50 text-blue-400 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                  />
                );
              })}
            </MobileCompanyNavbarContent>
          </CompanyNavbarContent>
        </CompanyNavbarPortal>

        <OverdueSubscriptionStickyBanner />

        <div className="py-4">{children}</div>
      </div>
    </Provider>
  );
}

export function getMenuOptions(selfie: Selfie, tickerSymbol: string, context: CompanyContextEnum, intl: IntlShape) {
  if (CompanyContextEnum.BrazilianCompany === context) {
    return getBrazilianMenus(selfie, tickerSymbol, intl);
  }

  if (CompanyContextEnum.AmericanCompany === context) {
    return getAmericanMenus(selfie, tickerSymbol, intl);
  }

  return getFiisMenus(selfie, tickerSymbol, intl);
}

function getHomeUrl(tickerSymbol: string, context: CompanyContextEnum) {
  if (CompanyContextEnum.BrazilianCompany === context) {
    return format("/br/%s", tickerSymbol.toLowerCase());
  }

  if (CompanyContextEnum.AmericanCompany === context) {
    return format("/us/%s", tickerSymbol.toLowerCase());
  }

  return format("/fiis/%s", tickerSymbol.toLowerCase());
}

interface TickerSymbolSwitchProps {
  tickerSymbol: string;
  context: CompanyContextEnum;
}

function TickerSymbolSwitch({ tickerSymbol, context }: TickerSymbolSwitchProps) {
  const [isOpen, setIsOpen] = useState(false);

  const issuer = useRootIssuer();
  const { pathname } = useRouter();
  const searchParams = useSearchParams();
  const tab = searchParams.get("tab");

  const allTickers = getAllTickersInSecurities(issuer);
  const vertical = getVerticalByContext(context);

  const hasMultipleSecurities = allTickers.length > 1;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger disabled={!hasMultipleSecurities}>
        <div className="inline-flex space-x-1 rounded-md bg-violet-50 px-2 py-1 dark:bg-orange-300/30">
          <h3 className="text-sm font-bold text-violet-600 dark:text-orange-100 sm:text-base">
            {tickerSymbol.toUpperCase()}
          </h3>

          <div
            className={classNames("flex items-center text-violet-600 focus-visible:outline-none dark:text-orange-100", {
              "rotate-180 transform": isOpen,
              hidden: !hasMultipleSecurities,
            })}
          >
            <ChevronDownIcon size={16} className="text-violet-600 dark:text-orange-100" strokeWidth={3} />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={classNames(
          "rounded-xl border border-gray-300 p-0 font-aeonik dark:border-slate-900 dark:bg-slate-800",
        )}
      >
        <ul className="list-none space-y-1 px-2 py-2 text-sm font-medium text-gray-500 outline-none dark:text-slate-400">
          {allTickers.map((ticker) => {
            const links = getTickerSymbolSwitchUrl(ticker, vertical, pathname, tab);

            return (
              <li key={ticker}>
                <Link
                  {...links}
                  className="inline-flex w-full rounded-md px-3 py-2 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-600/10 dark:hover:text-violet-200"
                  onClick={() => setIsOpen(false)}
                >
                  {ticker.toUpperCase()}
                </Link>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function getAllTickersInSecurities(
  issuer:
    | IAmericanCompanyIssuerTopLevelFragment
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment,
) {
  if (issuer.__typename === "BrazilianRealEstateFund") {
    return [issuer.tickerSymbol];
  }

  return issuer.securities.map((security) => security.tickerSymbol);
}

function getVerticalByContext(context: CompanyContextEnum) {
  if (CompanyContextEnum.BrazilianCompany === context) {
    return IPlatformVertical.Acoes;
  }

  if (CompanyContextEnum.AmericanCompany === context) {
    return IPlatformVertical.Stocks;
  }

  return IPlatformVertical.Fiis;
}

interface MobileCompanyNavbarHeaderProps {
  tickerSymbol: string;
  context: CompanyContextEnum;
  menus: MenuOption[];
}

export function MobileCompanyNavbarHeader({ tickerSymbol, context, menus }: MobileCompanyNavbarHeaderProps) {
  const [isOpen, setIsOpen] = useCompanyNavbarMobileIsOpen();

  const intl = useIntl();
  const issuer = useRootIssuer();
  const pathname = usePathname();

  const getContentLabel = () => {
    if (pathname === getHomeUrl(tickerSymbol, context)) {
      return intl.formatMessage({ defaultMessage: "Início", id: "m42V4+" });
    }

    return menus.find((menu) => menu.href === pathname)?.label;
  };

  return (
    <div
      className={classNames(
        "sticky top-navbar-mobile-offset z-20",
        "flex w-full justify-start",
        "border-b border-transparent bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900",
      )}
    >
      <div className="flex w-full items-center justify-between space-x-2">
        <CompanyNavbarMenuOption
          key="companyNavbar/home"
          url={getHomeUrl(tickerSymbol, context)}
          isActive={false}
          className="flex flex-row items-center gap-x-2 "
        >
          <Avatar
            src={issuer.niceLogo?.url || null}
            hashValue={issuer._id}
            alt={issuer.name}
            size={42}
            squircle={true}
            className="md:inline-flex"
            priority={true}
          />

          <TickerSymbolSwitch tickerSymbol={tickerSymbol} context={context} />
        </CompanyNavbarMenuOption>

        {!isOpen ? (
          <MobileCompanyNavbarTrigger>
            <h2 className="font-bold tracking-wider dark:text-slate-200">{getContentLabel()}</h2>

            <ChevronDownIcon size={16} className="dark:text-slate-400" />
          </MobileCompanyNavbarTrigger>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            className="rounded-lg bg-slate-200 p-1 dark:bg-slate-700"
          >
            <XIcon strokeWidth={2} className="block h-5 w-5 dark:text-slate-300" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function CompanyDiscussionsCouner() {
  const intl = useIntl();

  const { engagementStats } = useRootIssuer();
  const { viewerUnseenDiscussionsCommentsCount: unreadUnseenCount } = engagementStats;

  const FIRST_TIER_CAP = 99; // if the user has more than 99 notifications we'll cap the number to `SECOND_TIER_CAP`
  const SECOND_TIER_CAP = 9;

  const cappedUnreadUnseenCount = Math.min(Math.max(unreadUnseenCount, 0), FIRST_TIER_CAP);

  return (
    <SimpleTextTooltip content={intl.formatMessage({ defaultMessage: "Novos comentários", id: "yIezCC" })}>
      <div className="absolute right-1 top-1 box-border appearance-none lg:-top-1.5 lg:right-3">
        <span
          className={classNames(
            "inline-flex h-4 w-4 select-none items-center justify-center",
            "rounded-md bg-blue-500 text-[10px] font-bold text-white",
          )}
        >
          {unreadUnseenCount > cappedUnreadUnseenCount ? `${SECOND_TIER_CAP}+` : cappedUnreadUnseenCount}
        </span>
      </div>
    </SimpleTextTooltip>
  );
}
